import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaClock, FaUsers, FaGraduationCap, FaHeart, FaBookOpen, FaChild, FaChalkboardTeacher, FaAward, FaPray, FaComments, FaHandsHelping, FaOm, FaUserFriends } from "react-icons/fa";

const DaycareSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('services-section');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      id: 1,
      title: "Personalized Coaching",
      subtitle: "Individual Attention for Every Child",
      icon: FaChalkboardTeacher,
      color: "from-amber-400 to-orange-500",
      bgColor: "bg-gradient-to-br from-amber-50 to-orange-50",
      features: [
        { icon: FaGraduationCap, text: "Service to Sr.KG to 7th Std." },
        { icon: FaBookOpen, text: "Assistant for school homework" },
        { icon: FaChild, text: "Holistic Development" },
        { icon: FaAward, text: "Weekly Test & Assessment" },
        { icon: FaHeart, text: "Student Counselling" }
      ],
      timing: "Flexible Hours",
      ageGroup: "5-12 years"
    },
    {
      id: 2,
      title: "The Fun Hub!",
      subtitle: "Bagless Homeschooling Experience",
      icon: FaChild,
      color: "from-emerald-400 to-teal-500",
      bgColor: "bg-gradient-to-br from-emerald-50 to-teal-50",
      features: [
        { icon: FaClock, text: "Timing: 11:00 am - 1:00 pm" },
        { icon: FaUsers, text: "Age Group: 3 years to 5 years" },
        { icon: FaHeart, text: "Learning meets fun" },
        { icon: FaGraduationCap, text: "Advanced School Readiness" },
        { icon: FaBookOpen, text: "No heavy bags policy" }
      ],
      timing: "11:00 AM - 1:00 PM",
      ageGroup: "3-5 years"
    },
    {
      id: 3,
      title: "Sanskar Varg",
      subtitle: "Values & Moral Education",
      icon: FaPray,
      color: "from-purple-400 to-indigo-500",
      bgColor: "bg-gradient-to-br from-purple-50 to-indigo-50",
      features: [
        { icon: FaOm, text: "Daily Meditation & Yoga" },
        { icon: FaBookOpen, text: "Moral Stories & Values" },
        { icon: FaHeart, text: "Character Building" },
        { icon: FaUsers, text: "Cultural Activities" },
        { icon: FaGraduationCap, text: "Life Skills Development" }
      ],
      timing: "1 Hours",
      ageGroup: "All Ages"
    },
    {
      id: 4,
      title: "Counseling Services",
      subtitle: "Guidance & Support System",
      icon: FaComments,
      color: "from-blue-400 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50",
      features: [
        { icon: FaUserFriends, text: "Student Counseling" },
        { icon: FaHandsHelping, text: "Parent Guidance" },
        { icon: FaHeart, text: "Emotional Support" },
        { icon: FaGraduationCap, text: "Academic Guidance" },
        { icon: FaChild, text: "Behavioral Support" }
      ],
      timing: "By Appointment",
      ageGroup: "All Ages"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="services-section" className="py-20 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-indigo-100 text-indigo-700 px-6 py-2 rounded-full text-sm font-semibold mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isVisible ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FaHeart />
            <span>Our Services</span>
          </motion.div>

          <h2 className="text-4xl lg:text-6xl font-bold font-platypi text-gray-800 mb-6">
            <span className="gradient-text bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Nurturing
            </span>
            <br />
            <span className="text-gray-700">Every Child</span>
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-4">
            <q className="italic font-sedan text-2xl text-indigo-600">A Mother's Heart, a Teacher's Touch</q>
          </p>

          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive educational services designed to support your child's academic
            and personal growth in a loving, nurturing environment.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          className="grid md:grid-cols-2 xl:grid-cols-2 gap-8 lg:gap-12"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={service.id}
                variants={cardVariants}
                className="group h-full"
              >
                <div className="relative bg-white rounded-3xl p-8 h-full shadow-xl hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2 border border-gray-100 overflow-hidden">
                  {/* Gradient Background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                  {/* Decorative Elements */}
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
                  <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-xl group-hover:scale-125 transition-transform duration-700"></div>

                  {/* Header with Icon */}
                  <div className="relative z-10 mb-8">
                    <div className="flex items-center justify-between mb-4">
                      <motion.div
                        className={`w-20 h-20 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:shadow-xl transition-all duration-300`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <IconComponent size={32} />
                      </motion.div>
 
                    </div>

                    <div>
                      <h3 className="text-3xl font-bold font-platypi text-gray-800 mb-3 group-hover:text-gray-900 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 font-medium text-lg leading-relaxed">
                        {service.subtitle}
                      </p>
                    </div>
                  </div>

                  {/* Service Info Cards */}
                  <div className="relative z-10 grid grid-cols-2 gap-4 mb-8">
                    <motion.div
                      className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 group-hover:border-gray-300 transition-all duration-300"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className={`w-10 h-10 mx-auto mb-3 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center text-white shadow-md`}>
                        <FaClock size={18} />
                      </div>
                      <div className="text-sm font-bold text-gray-800 mb-1">{service.timing}</div>
                      <div className="text-xs text-gray-500 font-medium">Schedule</div>
                    </motion.div>

                    <motion.div
                      className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 group-hover:border-gray-300 transition-all duration-300"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className={`w-10 h-10 mx-auto mb-3 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center text-white shadow-md`}>
                        <FaUsers size={18} />
                      </div>
                      <div className="text-sm font-bold text-gray-800 mb-1">{service.ageGroup}</div>
                      <div className="text-xs text-gray-500 font-medium">Age Group</div>
                    </motion.div>
                  </div>

                  {/* Features List */}
                  <div className="relative z-10 space-y-4 mb-8">
                    {service.features.map((feature, idx) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <motion.div
                          key={idx}
                          className="flex items-center space-x-4 p-3 rounded-xl bg-white/50 border border-gray-100 hover:border-gray-200 hover:bg-white/80 transition-all duration-300 group/feature"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: idx * 0.1 }}
                          whileHover={{ x: 5 }}
                        >
                          <div className={`w-10 h-10 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center text-white shadow-md group-hover/feature:shadow-lg transition-shadow duration-300`}>
                            <FeatureIcon size={16} />
                          </div>
                          <span className="text-gray-700 font-semibold text-sm group-hover/feature:text-gray-800 transition-colors">
                            {feature.text}
                          </span>
                          <div className="ml-auto">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* CTA Section */}
                  <div className="relative z-10 space-y-4">
                    <motion.a
                      href={`https://wa.me/919834387679?text=Hi! I want to enroll my child in *${service.title}* (${service.subtitle}). Please provide more details about admission process, fees, and availability. Thank you!`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full bg-gradient-to-r ${service.color} text-white py-4 px-6 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2 group/btn block`}
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span>Enroll Now</span>
                      <motion.div
                        className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center"
                        animate={{ x: [0, 4, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                        </svg>
                      </motion.div>
                    </motion.a>

                    {/* Card Footer */}
                    {/* <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span>Available Now</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                     
                      </div>
                    </div> */}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-soft max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Ready to Give Your Child the Best Start?
            </h3>
            <p className="text-gray-600 mb-6">
              Join our loving community where every child is valued, nurtured, and encouraged to reach their full potential.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="https://wa.me/919834387679?text=Hi! I want to enroll my child at Smart Educare Center. Please provide detailed information about available programs, admission process, fees, schedule, and next steps for enrollment. Thank you!"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold shadow-medium hover:shadow-strong transition-all duration-300 flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                </svg>
                <span>Enroll Now</span>
              </motion.a>
              <motion.a
                href="https://wa.me/919834387679?text=Hi! I would like to schedule a visit to Smart Educare Center to see the facilities, meet the teachers, and learn more about your programs. Please let me know available dates and times for a center tour. Thank you!"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-indigo-600 text-indigo-600 px-8 py-3 rounded-xl font-semibold hover:bg-indigo-600 hover:text-white transition-all duration-300 flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
                <span>Schedule Visit</span>
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DaycareSection;
